/**
 * Student Degrees Page Component
 * Displays student's enrolled degree programs using the 4-panel read-only card layout
 */

import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactService } from '../../core/services/contact.service';
import { AuthService } from '../../services/auth.service';
import { ContactDegreeDetail } from '../../core/models';
import { DegreesSectionComponent } from './degrees-section.component';
import { TranslatePipe, I18nStore } from '../../core/features/translations';

import { PortalUiService } from '../../services/portal-ui.service';

@Component({
  selector: 'app-student-degrees',
  standalone: true,
  imports: [
    CommonModule,
    DegreesSectionComponent,
    TranslatePipe,
  ],
  template: `
    <div class="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in font-sans">


      <!-- Loading State -->
      @if (loading()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div class="w-12 h-12 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin"></div>
          <p class="text-gray-600 text-base m-0">{{ 'portal.degrees.loading' | translate }}</p>
        </div>
      } @else if (error()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-red-100 shadow-sm">
          <span class="material-symbols-outlined text-red-500 text-5xl">error</span>
          <p class="text-gray-700 text-base m-0 font-semibold">{{ error() }}</p>
          <button 
            type="button"
            (click)="loadDegrees()"
            class="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-all focus:outline-none"
          >
            <span class="material-symbols-outlined text-lg">refresh</span>
            {{ 'portal.degrees.retry' | translate }}
          </button>
        </div>
      } @else {
        <app-degrees-section [degrees]="degrees()" />
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DegreesComponent implements OnInit {
  private readonly contactService = inject(ContactService);
  private readonly authService = inject(AuthService);
  private readonly uiService = inject(PortalUiService);
  private readonly i18n = inject(I18nStore);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly degrees = signal<ContactDegreeDetail[]>([]);

  ngOnInit(): void {
    this.loadDegrees();
  }

  async loadDegrees(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const userInfo = this.authService.getUserInfo();
      const userEmail = userInfo?.sub || userInfo?.email;

      if (!userEmail) {
        throw new Error('User email not found');
      }

      const contact = await this.contactService.getContactByEmail(userEmail).toPromise();

      if (!contact || !contact.contact_id) {
        throw new Error('Contact not found for user');
      }

      const completeData = await this.contactService
        .getContactComplete(contact.contact_id)
        .toPromise();

      const degreesList = completeData?.degrees || [];
      this.degrees.set(degreesList);
      this.uiService.setPageCount(degreesList.length);
      this.loading.set(false);
    } catch (error) {
      console.error('❌ Error loading student degrees:', error);
      this.error.set(
        error instanceof Error ? error.message : this.i18n.t('portal.degrees.error.loading')
      );
      this.loading.set(false);
    }
  }
}
